import type { SourceId, SourceSyncPreviewIdentifiers, SourceSyncPreviewResponse, Workspace } from './api';
import {
  createSource,
  createSourceApiKey,
  createSourceOAuthConnection,
  createSourceSync,
  deleteSource,
  deleteSourceApiKey,
  deleteSourceOAuthConnection,
  deleteSourceSync,
  getMessageMetricsSourceSettings,
  getSource,
  getSourceDestinations,
  getSourceEventById,
  getSourceEvents,
  getSourceMetrics,
  getSourceOAuthConnection,
  getSourceOAuthProviderConfig,
  getSourceOptionManifest,
  getSourceSchema,
  getSourcesLastEvents as getSourcesLastSeen,
  getSourceStatus,
  getSourceSync,
  listSourceApiKeys,
  listSourceOptions,
  listSources,
  listSourceSyncRuns,
  getSourceSyncRunErrorFileUrl,
  listSourceSyncs,
  listSourceSyncsForSource,
  previewSourceSync,
  runSourceSync,
  setSourceDestinationConnection,
  toggleSourceSync,
  updateMessageMetricsSourceSettings,
  updateSource,
  updateSourceDestinationConnection,
  updateSourceSync,
  validateWhereClause,
  getWorkspace,
  listObjectTypes,
  createObjectTypeApi,
} from './api';

type ContinuationMeta = {
  continuation?: { first?: string; last?: string };
  pagination?: { page: number; total: number };
};

function getPreviousPageParam(_: unknown, allPages: { meta: ContinuationMeta }[]) {
  const firstPage = allPages.find((p) => !isEmptyString(p.meta?.continuation?.first));
  if (firstPage != null) {
    return `-${firstPage.meta?.continuation?.first}`;
  }
}

function getNextPageParam(lastPage: { events: unknown[]; meta: ContinuationMeta }) {
  return lastPage?.meta?.continuation?.last;
}
import type {
  ConnectorSourceJourneysObjectMapping,
  ConnectorSourceRecord,
  ConnectorSourceRecordMapping,
  MessageMetricsSourceSettings,
  Source,
  SourceApiKey,
  SourceEvent,
  SourceSync,
} from '.';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifyOnError } from '@/services/cdp-api';
import { useOAuthUpgrade } from '@/features/pipelines/oauth';
import type { ComputedStatus, StatusDetails } from '@/features/pipelines/utils';
import { computeStatus } from '@/features/pipelines/utils';
import { useParams } from '@/services/router/compat';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { generatePath } from '@/services/router/generate-path';
import type { Destination } from '../destinations';
import type { MetricsDuration } from '@/utils/time';
import type { AxiosError } from 'axios';
import type { PaginationRequest } from '@/hooks/pipelines/use-paginated-query';
import { usePaginatedQuery } from '@/hooks/pipelines/use-paginated-query';
import { listConnectorSourceRecords } from './api';
import type { CreateOrUpdateSourceSyncCmd } from '.';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import type { AvailableSource } from './types';
import { PlanFeature, useIsPlanFeatureEnabled } from '@/features/pipelines/utils';
import { snakeCase, uniq } from 'lodash-es';
import { sourceCategorySorter } from './utils/source-options';
import { useCallback, useMemo } from 'react';
import { isEmptyString } from '@/utils/string';

type ContinuationRequestExtended = {
  after?: string;
  before?: string;
};

type Meta = {
  continuation?: {
    first?: string;
    last?: string;
  };
  pagination?: {
    page: number;
    total: number;
  };
};

const DEFAULT_AUTO_REFRESH_INTERNVAL = 5_000;

type NullableSourceId = SourceId | null | undefined;

export const useSourceIdFromParams = () => {
  const { sourceId } = useParams();
  if (sourceId == null) {
    throw new Error('This hook should be used inside /sources/:sourceId route');
  }
  if (sourceId !== 'journeys') {
    const parsed = Number.parseInt(sourceId);
    if (Number.isNaN(parsed)) {
      // The sourceId has been mistyped by the user. If we let requests to the backend
      // with `/sources/NaN`, it will return a 400. Forces a 404 by passing a non-existing sourceId.
      return -1;
    }
    return parsed;
  }
  return sourceId;
};

export const useGetSource = (workspaceId: number | null, sourceId: NullableSourceId) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['sources', { id: sourceId, workspaceId: workspaceId }],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null) {
        return Promise.reject();
      }
      return getSource(workspaceId, sourceId);
    },
    enabled: workspaceId != null && sourceId != null,
    staleTime: 30 * 1000,
    initialData: () => {
      if (workspaceId == null) {
        return undefined;
      }

      return queryClient.getQueryData<Source[]>(['sources', 'list', { workspaceId }])?.find((source: Source) => {
        return source.id === sourceId;
      });
    },
    initialDataUpdatedAt: () => {
      if (workspaceId == null) {
        return undefined;
      }

      return queryClient.getQueryState(['sources', 'list', { workspaceId }])?.dataUpdatedAt;
    },
  });
};

export const useGetSourceStatus = (workspaceId: number | null, sourceId: NullableSourceId) => {
  return useQuery({
    queryKey: ['sources', { id: sourceId, workspaceId: workspaceId }, 'status'],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null) {
        return Promise.reject();
      }
      return getSourceStatus(workspaceId, sourceId);
    },
    enabled: workspaceId != null && sourceId != null,
  });
};

export const useComputeSourcesStatuses = (workspaceId: number | null, sources: Source[]) => {
  return useQuery({
    queryKey: ['sources', { workspaceId: workspaceId, sources: sources }, 'statuses'],
    queryFn: async () => {
      if (workspaceId == null || sources == null || sources.length === 0) {
        return Promise.reject();
      }
      return getSourcesLastSeen(workspaceId).then((sourcesLastSeen) => {
        const statuses: Record<number, StatusDetails> = {};
        for (const source of sources) {
          const lastSeen = sourcesLastSeen[`${source.id}`];
          const status = computeStatus(source.enabled, lastSeen ?? 0);
          statuses[source.id] = { status: status as ComputedStatus, lastSeen };
        }
        return statuses;
      });
    },
    enabled: workspaceId != null && sources != null && sources.length > 0,
  });
};

export const useGetSourceMetrics = (
  workspaceId: number | null,
  sourceId: NullableSourceId,
  event?: 'all' | 'identify' | 'track' | 'screen' | 'group' | 'alias' | 'page',
  duration?: MetricsDuration,
) => {
  return useQuery({
    queryKey: ['sources', { id: sourceId, workspaceId: workspaceId }, 'metrics', { event, duration }],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null) {
        return Promise.reject();
      }
      return getSourceMetrics(workspaceId, sourceId, event, duration);
    },
    enabled: workspaceId != null && sourceId != null,
  });
};

export const useGetSourceSchema = (
  workspaceId: number | null,
  sourceId: NullableSourceId,
  event?: 'identify' | 'track' | 'screen' | 'group' | 'alias' | 'page',
) => {
  return useQuery({
    queryKey: ['sources', { id: sourceId, workspaceId: workspaceId }, 'schema', { event }],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null) {
        return Promise.reject();
      }
      return getSourceSchema(workspaceId, sourceId, event);
    },
    enabled: workspaceId != null && sourceId != null,
  });
};

export const useGetSourceEvents = (
  workspaceId: number | null,
  sourceId: NullableSourceId,
  params: {
    limit?: number;
    eventTypes?: string[];
    recordTypes?: string[];
    syncNames?: string[];
    search?: string;
    messageId?: string;
    continuation?: ContinuationRequestExtended;
  } = {},
  options?: {
    enabled?: boolean;
  },
) => {
  return useInfiniteQuery({
    retry: false,
    getPreviousPageParam: getPreviousPageParam as (
      firstPage: { events: SourceEvent[]; meta: Meta },
      allPages: { events: SourceEvent[]; meta: Meta }[],
    ) => string | undefined,
    getNextPageParam: getNextPageParam as (lastPage: { events: SourceEvent[]; meta: Meta }) => string | undefined,
    initialPageParam: undefined as string | undefined,
    queryKey: ['sources', { id: sourceId, workspaceId: workspaceId }, 'events', params],
    queryFn: async ({ pageParam }): Promise<{ events: SourceEvent[]; meta: Meta }> => {
      if (workspaceId == null || sourceId == null) {
        return Promise.reject();
      }
      if (!isEmptyString(pageParam)) {
        params.continuation = pageParam.startsWith('-') ? { before: pageParam.slice(1) } : { after: pageParam };
      }
      const result = await getSourceEvents(workspaceId, sourceId, params);

      // When fetching the previous page, reject if there are no more pages
      // to prevent an empty page from being cached in data.pages.
      if (pageParam?.startsWith?.('-') === true && result.meta?.continuation?.first == null) {
        return Promise.reject();
      }

      return result;
    },
    enabled: workspaceId != null && sourceId != null && (options?.enabled ?? true),
  });
};

export const useGetSourceEventById = (
  workspaceId: number | null,
  sourceId?: number | null,
  messageId?: string | null,
  serverId?: string | null,
  position?: string | null,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey: ['sources', { id: sourceId, workspaceId: workspaceId }, 'eventById', messageId],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null || messageId == null || serverId == null || position == null) {
        return Promise.reject();
      }
      return getSourceEventById(workspaceId, sourceId, messageId, serverId, position);
    },
    enabled:
      workspaceId != null &&
      sourceId != null &&
      messageId != null &&
      serverId != null &&
      position != null &&
      (options?.enabled ?? true),
  });
};

export const useCreateSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      source,
    }: {
      workspaceId: number;
      source: {
        name: string;
        optionId: string;
        settings?: Record<string, unknown>;
      };
    }) => {
      return createSource(workspaceId, {
        optionId: source.optionId,
        name: (source.name ?? '').trim(),
        settings: source.settings,
      });
    },
    onSuccess: (dbSource, { workspaceId }) => {
      queryClient.setQueryData(['sources', { id: dbSource.id, workspaceId }], dbSource);
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['sources', 'list', { workspaceId }],
        }),
        queryClient.invalidateQueries({
          queryKey: ['destinations', 'list', { workspaceId }],
          refetchType: 'all',
        }),
      ]);
    },
    onError: notifyOnError,
  });
};

export const useUpdateSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      workspaceId: number;
      sourceId: number;
      source: {
        name: string;
        enabled: boolean;
        settings?: Record<string, unknown>;
        geolocation_enabled?: boolean;
      };
    }) => {
      return updateSource(data.workspaceId, data.sourceId, {
        ...data.source,
        name: (data.source.name ?? '').trim(),
      });
    },
    onSuccess: (dbSource, { workspaceId }) => {
      queryClient.setQueryData(['sources', { id: dbSource.id, workspaceId }], dbSource);
      queryClient.setQueriesData(
        { queryKey: ['sources', 'list', { workspaceId }] },
        (sources: Source[] | undefined) => {
          if (!Array.isArray(sources)) return sources;
          return sources.map((oldSource) => {
            if (oldSource.id === dbSource.id) {
              return dbSource;
            }

            return oldSource;
          });
        },
      );
    },
    onError: notifyOnError,
  });
};

export const useDeleteSource = (options?: { ignoreErrors?: boolean }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, sourceId }: { workspaceId: number; sourceId: number }) => {
      return deleteSource(workspaceId, sourceId);
    },
    onSuccess: (_data: any, { workspaceId, sourceId }: { workspaceId: number; sourceId: number }) => {
      queryClient.removeQueries({ queryKey: ['sources', { id: sourceId, workspaceId }] });
      queryClient.setQueriesData(
        { queryKey: ['sources', 'list', { workspaceId }] },
        (sources: Source[] | undefined) => {
          if (!Array.isArray(sources)) return sources;
          return sources.filter((source) => {
            return source.id !== sourceId;
          });
        },
      );

      // Update existing destinations by removing the connection with this source
      queryClient.setQueriesData(
        { queryKey: ['destinations', 'list', { workspaceId }] },
        (destinations: Destination[] | undefined) => {
          if (!Array.isArray(destinations)) return destinations;
          return destinations.map((destination) => {
            if (destination.source_ids.includes(sourceId)) {
              destination.source_ids = destination.source_ids.filter((id) => id !== sourceId);
            }
            return destination;
          });
        },
      );
    },
    onError:
      options?.ignoreErrors === true
        ? () => {
            // no op
          }
        : notifyOnError,
  });
};

export const useListSources = (workspaceId: number | null) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['sources', 'list', { workspaceId }],
    queryFn: async () => {
      if (workspaceId == null) {
        return Promise.reject();
      }
      const sources = await listSources(workspaceId);
      for (const source of sources) {
        queryClient.setQueryData(['sources', { id: source.id, workspaceId }], source);
      }
      return sources;
    },
    enabled: workspaceId != null,
  });
};

export const useSetSourceDestinationConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      sourceId,
      destinationId,
      connect,
      settings,
    }: {
      workspaceId: number;
      sourceId: number;
      destinationId: number;
      connect: boolean;
      settings?: ConnectorSourceJourneysObjectMapping;
    }) => {
      return setSourceDestinationConnection(workspaceId, sourceId, destinationId, connect, settings);
    },
    onSuccess: (
      _data: any,
      variables: {
        workspaceId: number;
        sourceId: number;
        destinationId: number;
        connect: boolean;
        settings?: ConnectorSourceJourneysObjectMapping;
      },
    ) => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['destinations'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['sources'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['sources', { id: variables.sourceId, workspaceId: variables.workspaceId }, 'destinations', 'list'],
        }),
        queryClient.setQueryData(
          ['sources', { id: variables.sourceId, workspaceId: variables.workspaceId }],
          (source: Source | undefined) => {
            if (source == null) {
              return;
            }
            source.destination_ids = variables.connect
              ? [...source.destination_ids, variables.destinationId]
              : source.destination_ids.filter((id) => id !== variables.destinationId);
            return source;
          },
        ),
        queryClient.setQueryData(
          ['destinations', { id: variables.destinationId, workspaceId: variables.workspaceId }],
          (destination: Destination | undefined) => {
            if (destination == null) {
              return;
            }

            destination.source_ids = variables.connect
              ? [...destination.source_ids, variables.sourceId]
              : destination.source_ids.filter((id) => id !== variables.sourceId);
            return destination;
          },
        ),
      ]);
    },
    onError: notifyOnError,
  });
};

export const useListSourceApiKeys = (workspaceId: number | null, sourceId: NullableSourceId) => {
  return useQuery({
    queryKey: ['sources', { id: sourceId, workspaceId }, 'apiKeys', 'list'],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null) {
        return Promise.reject();
      }
      return listSourceApiKeys(workspaceId, sourceId);
    },
    enabled: workspaceId != null && sourceId != null,
    staleTime: 60 * 1000,
  });
};

export const useCreateSourceApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, sourceId }: { workspaceId: number; sourceId: number }) => {
      return createSourceApiKey(workspaceId, sourceId);
    },
    onSuccess: (_data: any, { workspaceId, sourceId }: { workspaceId: number; sourceId: number }) => {
      return queryClient.invalidateQueries({
        queryKey: ['sources', { id: sourceId, workspaceId }, 'apiKeys', 'list'],
      });
    },
    onError: notifyOnError,
  });
};

export const useDeleteSourceApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, sourceId, apiKeyId }: { workspaceId: number; sourceId: number; apiKeyId: number }) => {
      return deleteSourceApiKey(workspaceId, sourceId, apiKeyId);
    },
    onSuccess: (
      _data: any,
      { workspaceId, sourceId, apiKeyId }: { workspaceId: number; sourceId: number; apiKeyId: number },
    ) => {
      queryClient.setQueriesData(
        { queryKey: ['sources', { id: sourceId, workspaceId }, 'apiKeys', 'list'] },
        (apiKeys: SourceApiKey[] | undefined) => {
          if (!Array.isArray(apiKeys)) return apiKeys;
          return apiKeys.filter((apiKey) => {
            return apiKey.id !== apiKeyId;
          });
        },
      );
    },
    onError: notifyOnError,
  });
};

export const useListSourceOptions = (workspaceId: number | null, { includeHidden = true } = {}) => {
  return useQuery({
    queryKey: ['sourceOptions', { workspaceId, includeHidden }],
    queryFn: async () => {
      if (workspaceId == null) {
        return Promise.reject();
      }
      let sources = await listSourceOptions(workspaceId);

      if (!includeHidden) {
        sources = sources.filter((source) => !source.hidden);
      }
      return sources;
    },
    enabled: workspaceId != null,
    staleTime: 60 * 1000, // one minute
  });
};

export const useGetSourceOAuthConnection = (workspaceId: number | null, sourceId: NullableSourceId) => {
  return useQuery({
    queryKey: ['sources', { id: sourceId, workspaceId: workspaceId }, 'connect'],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null) {
        return Promise.reject();
      }
      return getSourceOAuthConnection(workspaceId, sourceId);
    },
    enabled: workspaceId != null && sourceId != null,
  });
};

/**
 * Resolves whether a source's OAuth connection is currently disconnected, by
 * composing the source, its option definition, its manifest (to detect oauth2),
 * and the OAuth connection state. Non-OAuth sources are never "disconnected".
 *
 * Shared by the connector add-wizard's sync button and the standalone
 * add-sync page, which both gate "add a sync" on a live connection.
 */
export const useSourceConnectionStatus = (workspaceId: number | null, sourceId: NullableSourceId) => {
  const source = useGetSource(workspaceId, sourceId);
  const sourceOptions = useListSourceOptions(workspaceId);
  const definition = (sourceOptions.data ?? []).find((option) => option.option_id === source.data?.option_id);
  const manifest = useGetSourceOptionManifest(workspaceId, source.data?.option_id);
  const isOAuth = manifest.data?.authentication?.scheme === 'oauth2';
  const oAuthState = useGetSourceOAuthConnection(workspaceId, isOAuth ? source.data?.id : null);

  return {
    isLoading: source.isLoading,
    isDisconnected: isOAuth && oAuthState.data?.connected !== true,
    definition,
  };
};

export const useGetSourceOAuthProviderConfig = (
  workspaceId: number | null,
  optionId: string | null,
  settings: Record<string, unknown> | null,
) => {
  return useQuery({
    queryKey: ['sources', 'connect', { optionId: optionId, workspaceId: workspaceId, settings }],
    queryFn: async () => {
      if (workspaceId == null || optionId == null) {
        return Promise.reject();
      }
      return getSourceOAuthProviderConfig(workspaceId, optionId, settings);
    },
    enabled: workspaceId != null && optionId != null,
    retry: (_, error: AxiosError) => error?.response?.status !== 400,
  });
};

export const useCreateSourceOAuthConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      sourceId,
      payload,
    }: {
      workspaceId: number;
      sourceId: number;
      payload: Record<string, any>;
    }) => {
      if (workspaceId == null || sourceId == null) {
        return Promise.reject();
      }

      return createSourceOAuthConnection(workspaceId, sourceId, payload).then(() => getSource(workspaceId, sourceId));
    },
    onSuccess: (_, { workspaceId, sourceId }) => {
      const sourceKey = ['sources', { id: sourceId, workspaceId }];
      const sourceListKey = ['sources', 'list', { workspaceId }];
      const connectionsKey = ['sources', { id: sourceId, workspaceId: workspaceId }, 'connect'];
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: sourceKey }),
        queryClient.invalidateQueries({ queryKey: sourceListKey }),
        queryClient.invalidateQueries({ queryKey: connectionsKey }),
      ]);
    },
    onError: notifyOnError,
  });
};

export const useDeleteSourceOAuthConnection = (workspaceId: number | null, sourceId: NullableSourceId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (workspaceId == null || sourceId == null) {
        return Promise.reject();
      }

      return deleteSourceOAuthConnection(workspaceId, sourceId);
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['sources', { id: sourceId, workspaceId: workspaceId }, 'connect'] });

      const sourceKey = ['sources', { id: sourceId, workspaceId }];
      const sourceListKey = ['sources', 'list', { workspaceId }];
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: sourceKey }),
        queryClient.invalidateQueries({ queryKey: sourceListKey }),
      ]);
    },
    onError: notifyOnError,
  });
};

export const useGetSourceOptionManifest = (workspaceId: number | null, optionId: string | null | undefined) => {
  return useQuery({
    queryKey: ['sourceOptions', 'manifest', { id: optionId, workspaceId }],
    queryFn: async () => {
      if (workspaceId == null || optionId == null) {
        return Promise.reject();
      }
      return getSourceOptionManifest(workspaceId, optionId);
    },
    enabled: workspaceId != null && optionId != null,
    staleTime: 60 * 1000, // one minute
  });
};

export const useCreateSourceSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      sourceId,
      sync,
    }: {
      workspaceId: number;
      sourceId: number;
      sync: CreateOrUpdateSourceSyncCmd;
    }) => {
      return createSourceSync(workspaceId, sourceId, sync);
    },
    onSuccess: (sync, { workspaceId, sourceId }) => {
      queryClient.setQueryData(['sourceSyncs', { id: sync.id, workspaceId, sourceId }], sync);
      return queryClient.invalidateQueries({
        queryKey: ['sourceSyncs', 'list', { workspaceId, sourceId }],
      });
    },
    onError: notifyOnError,
  });
};

export const useUpdateSourceSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      sourceId,
      syncId,
      sync,
      confirmRestart,
    }: {
      workspaceId: number;
      sourceId: number;
      syncId: number;
      sync: CreateOrUpdateSourceSyncCmd;
      confirmRestart?: boolean;
    }) => {
      return updateSourceSync(workspaceId, sourceId, syncId, sync, confirmRestart);
    },
    onSuccess: (sync, { workspaceId, sourceId }) => {
      queryClient.setQueryData(['sourceSyncs', { id: sync.id, workspaceId, sourceId }], sync);
      return queryClient.invalidateQueries({
        queryKey: ['sourceSyncs', 'list', { workspaceId, sourceId }],
      });
    },
    onError: notifyOnError,
  });
};

interface ToggleSourceSyncParams {
  workspaceId: number;
  sourceId: number;
  syncId: number;
  enabled: boolean;
}

export const useToggleSourceSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, sourceId, syncId, enabled }: ToggleSourceSyncParams) => {
      return toggleSourceSync(workspaceId, sourceId, syncId, enabled);
    },
    onError: notifyOnError,
    onSuccess: (data: SourceSync, { workspaceId, sourceId, syncId, enabled }: ToggleSourceSyncParams) => {
      queryClient.setQueryData(['sourceSyncs', { id: syncId, workspaceId, sourceId }], { ...data, enabled });
      return queryClient.invalidateQueries({
        queryKey: ['sourceSyncs', 'list', { workspaceId, sourceId }],
      });
    },
  });
};

export const useGetSourceSync = (workspaceId: number | null, sourceId: NullableSourceId, syncId: number | null) => {
  return useQuery({
    queryKey: ['sourceSyncs', { id: syncId, workspaceId, sourceId }],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null || syncId == null) {
        return Promise.reject();
      }
      return getSourceSync(workspaceId, sourceId, syncId);
    },
    enabled: workspaceId != null && sourceId != null && syncId != null,
  });
};

export const useListSourceSyncs = (
  workspaceId: number | null,
  params: {
    enabled?: boolean;
  } = {
    enabled: true,
  },
) => {
  return useQuery({
    queryKey: ['sourceSyncs', 'list', { workspaceId }],
    queryFn: async () => {
      if (workspaceId == null) {
        return Promise.reject();
      }
      return listSourceSyncs(workspaceId);
    },
    enabled: workspaceId != null && params.enabled,
  });
};

export const useListSourceSyncsForSource = (
  workspaceId: number | null,
  sourceId: NullableSourceId,
  params: {
    enabled?: boolean;
  } = {
    enabled: true,
  },
) => {
  return useQuery({
    queryKey: ['sourceSyncs', 'list', { workspaceId, sourceId }],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null) {
        return Promise.reject();
      }
      return listSourceSyncsForSource(workspaceId, sourceId);
    },
    enabled: workspaceId != null && params.enabled,
  });
};

export const useListSourceSyncRuns = (
  workspaceId: number | null,
  sourceId?: number | null,
  params: {
    page?: number;
    limit?: number;
    showOnlyErrors?: boolean;
    enabled?: boolean;
    autoRefresh?: boolean | number;
    syncImportId?: number;
    excludeEmptyRows?: boolean;
  } = {
    enabled: true,
  },
) => {
  const { autoRefresh } = params;
  const turnOnAutoRefresh = params.autoRefresh != null && params.autoRefresh != false;
  const refetchInterval =
    autoRefresh == null || autoRefresh == true || Number.isNaN(Number.parseInt(`${autoRefresh}`))
      ? DEFAULT_AUTO_REFRESH_INTERNVAL
      : Number.parseInt(`${autoRefresh}`);

  return usePaginatedQuery(
    {
      gcTime: 0,
      ...(turnOnAutoRefresh
        ? {
            refetchInterval,
            refetchIntervalInBackground: true,
          }
        : {}),
      queryKey: ['sources', 'syncRuns', { workspaceId, sourceId }, 'list', params],
      queryFn: async ({ meta }) => {
        if (workspaceId == null || sourceId == null) {
          return Promise.reject();
        }
        return listSourceSyncRuns(workspaceId, sourceId, {
          ...params,
          ...(meta?.pagination as PaginationRequest),
        });
      },
      enabled: workspaceId != null && sourceId != null && params.enabled,
    },
    {
      initialPage: params.page,
      pageSize: params.limit,
    },
  );
};

export const useGetSourceSyncRunErrorFileUrl = ({
  workspaceId,
  sourceId,
  syncRunId,
}: {
  workspaceId: number | null;
  sourceId: number | null;
  syncRunId: number | null;
}) => {
  return useQuery({
    queryKey: ['sources', 'syncRunErrorFileUrl', { workspaceId, sourceId, syncRunId }],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null || syncRunId == null) {
        return Promise.reject();
      }
      return getSourceSyncRunErrorFileUrl(workspaceId, sourceId, syncRunId);
    },
    enabled: workspaceId != null && sourceId != null && syncRunId != null,
  });
};

export const useDeleteSourceSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, sourceId, syncId }: { workspaceId: number; sourceId: number; syncId: number }) => {
      return deleteSourceSync(workspaceId, sourceId, syncId);
    },
    onSuccess: (
      _data: unknown,
      { workspaceId, sourceId, syncId }: { workspaceId: number; sourceId: number; syncId: number },
    ) => {
      queryClient.setQueriesData(
        { queryKey: ['sourceSyncs', 'list', { workspaceId, sourceId }] },
        (syncs: SourceSync[] | undefined) => {
          if (!Array.isArray(syncs)) return syncs;
          return syncs.filter((sync) => {
            return sync.id !== syncId;
          });
        },
      );
    },
    onError: notifyOnError,
  });
};

export const useListConnectorSourceRecords = (
  workspaceId: number | null,
  sourceId?: number | null,
  options: {
    enabled?: boolean;
    initialData?: ConnectorSourceRecord[];
  } = {
    enabled: true,
  },
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['connectorSourceRecords', 'list', { workspaceId, sourceId }],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null) {
        return Promise.reject();
      }
      const records = await listConnectorSourceRecords(workspaceId, sourceId);
      const source = queryClient.getQueryData<Source>(['sources', { id: sourceId, workspaceId }]);
      if (source?.option_id !== 'salesforce') {
        // The call to useGetConnectorSourceRecordDescriptor is unnecessary for hubspot
        // just build the cache
        for (const record of records) {
          const key = (includeRelationships: boolean) => [
            'connectorSourceRecords',
            'descriptor',
            {
              workspaceId,
              sourceId,
              record: record.name,
              includeRelationships,
            },
          ];
          queryClient.setQueryData(key(true), record);
          queryClient.setQueryData(key(false), record);
        }
      }
      return records;
    },
    enabled: options.enabled == true && workspaceId != null && sourceId != null,
    placeholderData: options.initialData,
    staleTime: 1000 * 60 * 60 * 15,
  });
};

export const useGetConnectorSourceRecordDescriptor = (
  workspaceId: number | null,
  sourceId?: number | null,
  record?: string | null,
  includeRelationships?: boolean,
  options: {
    enabled?: boolean;
    initialData?: ConnectorSourceRecord;
  } = {
    enabled: true,
  },
) => {
  return useQuery({
    queryKey: ['connectorSourceRecords', 'descriptor', { workspaceId, sourceId, record, includeRelationships }],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null || record == null) {
        return Promise.reject();
      }
      return listConnectorSourceRecords(workspaceId, sourceId, record, includeRelationships).then((response) => {
        if (response == null || response.length === 0) {
          return null;
        }
        return response[0];
      });
    },
    staleTime: 1000 * 60 * 60 * 15,
    initialData: options.initialData,
    enabled: options.enabled == true && workspaceId != null && sourceId != null && record != null && record != '',
  });
};

export const useRunSourceSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      sourceId,
      syncId,
      restart,
    }: {
      workspaceId: number;
      sourceId: number;
      syncId: number;
      restart?: boolean;
    }) => {
      return runSourceSync(workspaceId, sourceId, syncId, { restart });
    },
    onSuccess: ({ workspaceId, sourceId }: { workspaceId: number; sourceId: number }) => {
      return queryClient.invalidateQueries({
        queryKey: ['sourceSyncs', 'list', { workspaceId, sourceId }],
      });
    },
    onError: notifyOnError,
  });
};

export const useListSourceDestinationsSettings = (workspaceId: number | null, sourceId: NullableSourceId) => {
  return useQuery({
    queryKey: ['sources', { id: sourceId, workspaceId }, 'destinations', 'list'],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null) {
        return Promise.reject();
      }
      return getSourceDestinations(workspaceId, sourceId);
    },
    enabled: workspaceId != null && sourceId != null,
    staleTime: 60 * 1000,
  });
};

export const useUpdateSourceDestinationConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      sourceId,
      destinationId,
      settings,
    }: {
      workspaceId: number;
      sourceId: number;
      destinationId: number;
      settings?: ConnectorSourceJourneysObjectMapping;
    }) => {
      return updateSourceDestinationConnection(workspaceId, sourceId, destinationId, settings);
    },
    onSuccess: (_, { workspaceId, sourceId }) => {
      return queryClient.invalidateQueries({
        queryKey: ['sources', { id: sourceId, workspaceId }, 'destinations', 'list'],
      });
    },
    onError: notifyOnError,
  });
};

export function useGetSourceDescriptor(optionId?: string): [AvailableSource | undefined, boolean] {
  const [find, isLoading] = useFindSourceDescriptor();

  if (isLoading) {
    return [undefined, isLoading];
  }

  return [find(optionId), false];
}

/**
 * Returns a function to find source descriptors by optionId. Use this if you need to look for more then one optiondId.
 * @returns [
 *  find,        : function to find source by optionId
 *  isLoading,   : indicates if the source options are still loading
 *  categories   : list of all sources categories (including hidden ones)
 * ]
 */
export function useFindSourceDescriptor(): [(optionId?: string) => AvailableSource | undefined, boolean, string[]] {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  const sourceOptions = useListSourceOptions(workspaceId);
  const options = sourceOptions.data ?? [];
  const categories = useMemo(() => {
    const unique = uniq(options.map((o) => (o.category.length === 0 ? 'Other' : o.category)));
    const sorted = unique.sort(sourceCategorySorter);
    return sorted;
  }, [options.length]);

  const { isLoading } = sourceOptions;
  if (isLoading) {
    return [() => undefined, true, ['All']];
  }

  return [
    (optionId?: string) => {
      return options.find((s) => s.option_id === optionId);
    },
    false,
    categories,
  ];
}

export function useCanAddSalesforce() {
  const isPlanFeatureEnabled = useIsPlanFeatureEnabled(PlanFeature.CdpPremiumIntegrations).data;

  return isPlanFeatureEnabled === true;
}

export const useValidateWhereClause = () => {
  return useMutation({
    mutationFn: ({
      workspaceId,
      sourceId,
      sobject_name,
      where_clause,
      identifiers,
    }: {
      workspaceId: number;
      sourceId: number;
      sobject_name: string;
      where_clause: string;
      identifiers?: {
        user_id?: string;
        group_id?: string;
        cdp_type?: string;
        object_name?: string;
      };
    }) =>
      validateWhereClause(workspaceId, sourceId, {
        sobject_name,
        where_clause,
        identifiers,
      }),
    // Remove onError to let the component handle validation errors in-context
    // instead of showing generic toast notifications
  });
};

export const useGetMessageMetricsSourceSettings = (workspaceId: number | null, sourceId: NullableSourceId) => {
  const queryClient = useQueryClient();
  const key = ['sources', { id: sourceId, workspaceId: workspaceId }];
  const sourceData = queryClient.getQueryData<Source>(key);

  return useQuery({
    queryKey: ['sources', { id: sourceId, workspaceId }, 'settings'],
    queryFn: async () => {
      if (workspaceId == null || sourceId == null || typeof sourceId == 'string') {
        return Promise.reject();
      }
      return getMessageMetricsSourceSettings(workspaceId, sourceId);
    },
    enabled: workspaceId != null && sourceId != null && sourceData?.is_journeys_metrics_source,
    staleTime: 5 * 60 * 1000,
  });
};

export const useUpdateMessageMetricsSourceSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workspaceId,
      sourceId,
      settings,
    }: {
      workspaceId: number;
      sourceId: number;
      settings: MessageMetricsSourceSettings;
    }) => updateMessageMetricsSourceSettings(workspaceId, sourceId, settings),
    onSuccess: (_, { workspaceId, sourceId }) => {
      return queryClient.invalidateQueries({
        queryKey: ['sources', { id: sourceId, workspaceId }, 'settings'],
      });
    },
    onError: notifyOnError,
  });
};

export const useSourceSyncPreview = (
  workspaceId: number,
  sourceId: SourceId,
  params: {
    objectType?: string;
    cdpType?: string;
    identifiers?: SourceSyncPreviewIdentifiers;
    fields: string[];
    renames?: Record<string, string>;
    whereClause?: string;
    limit?: number;
    enabled?: boolean;
    keepPreviousData?: boolean;
  },
) => {
  return useQuery({
    queryKey: [
      'sources',
      { id: sourceId, workspaceId },
      'syncs',
      'preview',
      {
        objectType: params.objectType,
        cdpType: params.cdpType,
        identifiers: params.identifiers,
        fields: params.fields,
        renames: params.renames,
        whereClause: params.whereClause,
        limit: params.limit,
      },
    ],
    queryFn: async (): Promise<SourceSyncPreviewResponse> => {
      if (isEmptyString(params.objectType) || isEmptyString(params.cdpType)) {
        throw new Error('objectType and cdpType are required for preview');
      }
      return previewSourceSync(workspaceId, sourceId, {
        object_type: params.objectType,
        cdp_type: params.cdpType,
        identifiers: params.identifiers,
        fields: params.fields,
        renames: params.renames,
        where_clause: params.whereClause,
        limit: params.limit ?? 10,
      });
    },
    enabled: (params.enabled ?? true) && params.objectType != null && params.cdpType != null,
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: (params.keepPreviousData ?? false) ? keepPreviousData : undefined,
  });
};

export const useNavigateToAddSync = () => {
  const navigate = useSafeNavigate();
  const workspaceId = useWorkspaceIdFromCurrentUrl();

  return useCallback(
    (
      sourceId: SourceId,
      recordType?: string,
      mapping?: ConnectorSourceRecordMapping,
      options?: { openPreview?: boolean },
    ) => {
      if (workspaceId == null) return;
      const state = recordType != null ? { recordType, mapping, openPreview: options?.openPreview } : undefined;
      navigate(
        generatePath('/workspaces/:workspaceId/pipelines/sources/:sourceId/syncs/add', { workspaceId, sourceId }),
        state != null ? { state } : undefined,
      );
    },
    [navigate, workspaceId],
  );
};

export const useSourceOAuthUpgrade = (workspaceId: number, source: Source, settings?: Record<string, unknown>) => {
  const providerConfig = useGetSourceOAuthProviderConfig(
    workspaceId,
    source.option_id,
    settings ?? source.settings ?? null,
  );
  const doUpdateSource = useUpdateSource();
  const doCreateOAuthConnection = useCreateSourceOAuthConnection();

  return useOAuthUpgrade({
    optionId: source.option_id,
    providerConfig,
    updateEntity: () =>
      doUpdateSource.mutateAsync({
        workspaceId,
        sourceId: source.id,
        source: {
          ...source,
          settings: settings ?? source.settings,
        },
      }),
    createConnection: (response) =>
      doCreateOAuthConnection.mutateAsync({
        workspaceId,
        sourceId: source.id,
        payload: response,
      }),
    onError: notifyOnError,
  });
};

export { forceSourceOAuthTokenRefresh } from './api';
export { computeMappingDefaults } from './utils/mapping-defaults';
export { getPreviewErrorMessage } from './utils/preview';
export { SourceDescriptorProvider, useSourceDescriptor } from './context';
export { SalesforceInfoCard } from './components/salesforce-info-card';

// --- Workspace/object-type hooks (used by source wizards) ---

export const useGetWorkspace = (workspaceId: number | null) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['workspaces', { id: workspaceId }],
    queryFn: async () => {
      if (workspaceId == null) {
        return Promise.reject();
      }
      return getWorkspace(workspaceId);
    },
    enabled: workspaceId != null,
    staleTime: 60 * 1000,
    initialData: () => {
      const queries = queryClient.getQueriesData<Workspace[]>({ queryKey: ['workspaces', 'list'] });
      const [, workspaces] = queries[0] ?? [];
      return workspaces?.find((workspace) => workspace.id === workspaceId);
    },
    initialDataUpdatedAt: () => {
      const queries = queryClient.getQueriesData<Workspace[]>({ queryKey: ['workspaces', 'list'] });
      const [key] = queries[0] ?? [];
      return key != null ? queryClient.getQueryState(key)?.dataUpdatedAt : undefined;
    },
  });
};

export const useListWorkspaceObjectTypes = (workspaceId: number | null) => {
  return useQuery({
    queryKey: ['workspaces', { workspaceId }, 'objectTypes', 'list'],
    queryFn: async () => {
      if (workspaceId == null) {
        return Promise.reject();
      }
      return listObjectTypes(workspaceId);
    },
    enabled: workspaceId != null,
  });
};

export const useCreateObjectType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      journeysObjectDefinition,
    }: {
      workspaceId: number;
      journeysObjectDefinition: {
        pluralForm: string;
        singularForm: string;
      };
    }) => {
      const { pluralForm: name, singularForm: singularName } = journeysObjectDefinition;
      return createObjectTypeApi(workspaceId, {
        name,
        slug: snakeCase(name),
        singular_name: singularName,
        singular_slug: snakeCase(singularName),
      });
    },
    onSuccess: (_, variables) => {
      const { workspaceId } = variables;
      return queryClient.invalidateQueries({
        queryKey: ['workspaces', { workspaceId }, 'objectTypes', 'list'],
      });
    },
    onError: notifyOnError,
  });
};
